import { useCallback, useEffect, useState } from 'react';
import ApiService from '../../services/api.service';

const pick = (obj, camelKey, snakeKey, fallback = '') => {
  if (!obj) return fallback;
  const value = obj[camelKey] !== undefined ? obj[camelKey] : obj[snakeKey];
  return value !== undefined && value !== null ? value : fallback;
};

const normalizeCustomer = (row = {}) => ({
  id: row.id,
  name: row.name,
  type: row.type || '',
  emirate: row.emirate || '',
  trnNumber: pick(row, 'trnNumber', 'trn_number'),
  portalEnabled: pick(row, 'portalEnabled', 'portal_enabled', false) === true,
  active: row.active === true
});

export const usePortalCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(null);
  const [enabling, setEnabling] = useState(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const res = await ApiService.getPortalGroupCustomers({ page: 0, size: 100 });
        const list = (res.data?.data || []).map(normalizeCustomer);
        if (active) setCustomers(list);
      } catch (err) {
        if (active) setError(err.response?.data?.error || 'Failed to load your customers.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, []);

  const openCustomer = useCallback(async (customer) => {
    setError('');
    setOpening(customer.id);

    try {
      const res = await ApiService.portalGroupSwitch(customer.id);
      const data = res.data?.data;
      if (!data?.token) throw new Error('No token returned');

      const user = data.user || {};
      const organization = data.organization || {};

      localStorage.setItem('portalToken', data.token);
      localStorage.setItem('portalUser', JSON.stringify(user));
      localStorage.setItem('portalOrganization', JSON.stringify(organization));
      localStorage.setItem('portalAuthenticated', 'true');
      localStorage.setItem('actingAsPortalGroupUser', 'true');
      window.location.href = '/portal/dashboard';
    } catch (err) {
      setError(err.response?.data?.error || 'Could not open this customer.');
      setOpening(null);
    }
  }, []);

  // Turn on the portal of a group customer that has none yet; the card becomes openable in place.
  const enablePortal = useCallback(async (customer) => {
    setError('');
    setEnabling(customer.id);
    try {
      await ApiService.portalGroupEnableCustomerPortal(customer.id);
      setCustomers((prev) => prev.map((c) => (c.id === customer.id ? { ...c, portalEnabled: true } : c)));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not enable the portal for this customer.');
    } finally {
      setEnabling(null);
    }
  }, []);

  return { customers, loading, error, opening, openCustomer, enabling, enablePortal };
};

export default usePortalCustomers;
